import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import axios from 'axios';
import {
  clearStoredWorkspace,
  getStoredWorkspace,
  setStoredWorkspace,
} from './workspaceStorage';

const WorkspaceContext = createContext(null);

export function WorkspaceProvider({ children }) {
  const [workspace, setWorkspace] = useState(() => getStoredWorkspace());
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const login = useCallback(async (username) => {
    setLoginLoading(true);
    setLoginError('');
    try {
      const res = await axios.post('/api/workspace/login/', { username });
      const next = {
        workspaceId: res.data.workspace_id,
        displayName: res.data.display_name,
        usernameSlug: res.data.username_slug,
      };
      setStoredWorkspace(next);
      setWorkspace(next);
      setLoginOpen(false);
      return next;
    } catch (e) {
      const detail =
        e.response?.data?.detail ||
        'Could not sign in. Check the username and try again.';
      setLoginError(typeof detail === 'string' ? detail : 'Sign in failed.');
      throw e;
    } finally {
      setLoginLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    clearStoredWorkspace();
    setWorkspace(null);
  }, []);

  const openLogin = useCallback(() => {
    setLoginError('');
    setLoginOpen(true);
  }, []);

  const closeLogin = useCallback(() => {
    setLoginOpen(false);
    setLoginError('');
  }, []);

  const value = useMemo(
    () => ({
      workspace,
      isLoggedIn: Boolean(workspace?.workspaceId),
      displayName: workspace?.displayName || '',
      login,
      logout,
      loginOpen,
      openLogin,
      closeLogin,
      loginError,
      loginLoading,
    }),
    [
      workspace,
      login,
      logout,
      loginOpen,
      openLogin,
      closeLogin,
      loginError,
      loginLoading,
    ],
  );

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error('useWorkspace must be used within WorkspaceProvider');
  }
  return ctx;
}
